import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { X, User, CreditCard, Calendar, DollarSign, Info } from 'lucide-react';

interface AssinaturaModalProps {
  isOpen: boolean;
  onClose: () => void;
  assinaturaId?: string;
  onSuccess?: () => void;
  preloadedData?: {
    alunoId: string;
    valor: number;
    diaVencimento: number;
  };
}

interface AssinaturaFormData {
  alunoId: string;
  valor: number;
  descricao: string;
  formaPagamento: string;
  ciclo: string;
  dataInicio: string;
  dataFim?: string;
  jurosMensal: number;
  jurosFixo: number;
  multaTipo: string;
  multaPercentual: number;
  multaFixa: number;
  descontoTipo: string;
  descontoPercentual: number;
  descontoFixo: number;
  prazoDesconto: string;
  observacoes: string;
  enviarCorreios: boolean;
}

const AssinaturaModal: React.FC<AssinaturaModalProps> = ({
  isOpen,
  onClose,
  assinaturaId,
  onSuccess,
  preloadedData
}) => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [alunos, setAlunos] = useState<any[]>([]);
  const [formData, setFormData] = useState<AssinaturaFormData>({
    alunoId: '',
    valor: 0,
    descricao: '',
    formaPagamento: '',
    ciclo: 'MONTHLY',
    dataInicio: '',
    dataFim: '',
    jurosMensal: 0,
    jurosFixo: 0,
    multaTipo: 'PERCENTAGE',
    multaPercentual: 0,
    multaFixa: 0,
    descontoTipo: 'PERCENTAGE',
    descontoPercentual: 0,
    descontoFixo: 0,
    prazoDesconto: '',
    observacoes: '',
    enviarCorreios: false
  });

  const loadAlunos = React.useCallback(async () => {
    if (!userProfile?.empresaCnpj) return;
    
    try {
      const alunosData = await firebaseService.getAllAlunos(userProfile.empresaCnpj);
      setAlunos(alunosData);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
    }
  }, [userProfile?.empresaCnpj]);

  const loadAssinatura = React.useCallback(async () => {
    if (!assinaturaId) return;
    
    try {
      console.log('🔍 Carregando assinatura ID:', assinaturaId);
      const assinatura = await firebaseService.getAssinatura(assinaturaId, userProfile?.empresaCnpj);
      console.log('📋 Dados da assinatura:', assinatura);
      
      if (assinatura) {
        console.log('📅 nextDueDate:', assinatura.nextDueDate);
        console.log('📅 createDate:', assinatura.createDate);
        console.log('💰 valor:', assinatura.value);
        console.log('📝 descricao:', assinatura.description);
        console.log('💳 formaPagamento:', assinatura.billingType);
        
        const formDataToSet = {
          alunoId: assinatura.alunoId,
          valor: assinatura.value,
          descricao: assinatura.description || '',
          formaPagamento: assinatura.billingType,
          ciclo: assinatura.cycle,
          dataInicio: assinatura.nextDueDate || assinatura.createDate,
          dataFim: assinatura.deletedDate || '',
          jurosMensal: assinatura.interest || 0,
          jurosFixo: 0,
          multaTipo: 'PERCENTAGE',
          multaPercentual: assinatura.fine || 0,
          multaFixa: 0,
          descontoTipo: 'PERCENTAGE',
          descontoPercentual: assinatura.discount || 0,
          descontoFixo: 0,
          prazoDesconto: '',
          observacoes: assinatura.description || '',
          enviarCorreios: false
        };
        
        console.log('🔍 Mapeamento final:');
        console.log('📅 dataInicio final:', formDataToSet.dataInicio);
        console.log('💰 valor final:', formDataToSet.valor);
        console.log('📝 descricao final:', formDataToSet.descricao);
        console.log('💳 formaPagamento final:', formDataToSet.formaPagamento);
        
        console.log('📝 FormData a ser definido:', formDataToSet);
        console.log('📅 dataInicio final:', formDataToSet.dataInicio);
        setFormData(formDataToSet);
      } else {
        console.log('❌ Assinatura não encontrada');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar assinatura:', error);
    }
  }, [assinaturaId, userProfile?.empresaCnpj]);

  useEffect(() => {
    if (isOpen) {
      // Carregar alunos primeiro para garantir que estejam disponíveis quando a assinatura for carregada
      loadAlunos().then(() => {
        if (assinaturaId) {
          loadAssinatura();
        } else if (preloadedData) {
        // Carregar dados pré-carregados para aluno sem assinatura
        const hoje = new Date();
        const diaVencimento = preloadedData.diaVencimento;
        const anoAtual = hoje.getFullYear();
        const mesAtual = hoje.getMonth();
        
        // Calcular data da primeira cobrança baseada no dia de vencimento
        let dataPrimeiraCobranca = new Date(anoAtual, mesAtual, diaVencimento);
        
        // Se a data já passou neste mês, usar o próximo mês
        if (dataPrimeiraCobranca < hoje) {
          dataPrimeiraCobranca = new Date(anoAtual, mesAtual + 1, diaVencimento);
        }
        
          setFormData(prev => ({
            ...prev,
            alunoId: preloadedData.alunoId,
            valor: preloadedData.valor,
            dataInicio: dataPrimeiraCobranca.toISOString().split('T')[0], // Formato YYYY-MM-DD
          }));
        }
      });
    }
  }, [isOpen, assinaturaId, preloadedData, loadAlunos, loadAssinatura]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userProfile?.empresaCnpj) return;

    try {
      setLoading(true);
      
      if (assinaturaId) {
        console.log('🔄 Iniciando atualização da assinatura:', assinaturaId);
        console.log('📝 Dados do formulário:', formData);
        
        // Buscar dados atuais da assinatura para preservar campos importantes
        const assinaturaAtual = await firebaseService.getAssinatura(assinaturaId, userProfile.empresaCnpj);
        console.log('📋 Assinatura atual:', assinaturaAtual);
        
        // Atualizar assinatura existente - fazer merge com dados existentes
        const assinaturaData = {
          ...assinaturaAtual, // Preservar todos os campos existentes
          id: assinaturaId,
          alunoId: formData.alunoId,
          value: formData.valor,
          description: formData.descricao,
          billingType: formData.formaPagamento,
          cycle: formData.ciclo,
          nextDueDate: formData.dataInicio,
          deletedDate: formData.dataFim || null,
          status: 'ACTIVE',
          interest: formData.jurosMensal || 0,
          fine: formData.multaPercentual || 0,
          discount: formData.descontoPercentual || 0,
          updateDate: new Date().toISOString()
        };
        console.log('💾 Dados finais para salvar:', assinaturaData);
        console.log('📝 formData.descricao:', formData.descricao);
        console.log('📝 assinaturaData.description:', assinaturaData.description);
        
        await firebaseService.updateAssinatura(userProfile.empresaCnpj, assinaturaData);
        alert('Assinatura atualizada com sucesso!');
      } else {
        // Criar nova assinatura
        await firebaseService.createAssinatura(userProfile.empresaCnpj, formData);
        alert('Assinatura criada com sucesso!');
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Erro ao salvar assinatura:', error);
      alert('Erro ao salvar assinatura. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof AssinaturaFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {assinaturaId ? 'Editar Assinatura' : 'Adicionar Assinatura'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="bg-white dark:bg-gray-800 px-6 py-4 max-h-96 overflow-y-auto">
              {/* Dados da assinatura */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Dados da assinatura</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cliente
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <select
                        value={formData.alunoId}
                        onChange={(e) => handleInputChange('alunoId', e.target.value)}
                        className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        required
                      >
                        <option value="">Selecione o cliente</option>
                        {alunos.map(aluno => (
                          <option key={aluno.id} value={aluno.id}>
                            {aluno.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Forma de pagamento
                    </label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <select
                        value={formData.formaPagamento}
                        onChange={(e) => handleInputChange('formaPagamento', e.target.value)}
                        className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        required
                      >
                        <option value="">Selecione a forma de pagamento</option>
                        <option value="PIX">Pix</option>
                        <option value="BOLETO">Boleto Bancário</option>
                        <option value="CREDIT_CARD">Cartão de Crédito</option>
                        <option value="DEBIT_CARD">Cartão de Débito</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Em um intervalo
                    </label>
                    <select
                      value={formData.ciclo}
                      onChange={(e) => handleInputChange('ciclo', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="MONTHLY">Mensal</option>
                      <option value="QUARTERLY">Trimestral</option>
                      <option value="SEMIANNUAL">Semestral</option>
                      <option value="ANNUAL">Anual</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Com valor fixo de
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="number"
                        step="0.01"
                        value={formData.valor}
                        onChange={(e) => handleInputChange('valor', parseFloat(e.target.value) || 0)}
                        className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="0,00"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vencimento da primeira cobrança
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="date"
                        value={formData.dataInicio}
                        onChange={(e) => handleInputChange('dataInicio', e.target.value)}
                        className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data de fim de assinatura
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="date"
                        value={formData.dataFim}
                        onChange={(e) => handleInputChange('dataFim', e.target.value)}
                        className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Juros e multa */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Juros e multa</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Juros ao mês
                      <Info className="inline ml-1 h-4 w-4 text-gray-400" />
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={formData.jurosMensal}
                        onChange={(e) => handleInputChange('jurosMensal', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="0,00"
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor fixo de juros ao mês
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="number"
                        step="0.01"
                        value={formData.jurosFixo}
                        onChange={(e) => handleInputChange('jurosFixo', parseFloat(e.target.value) || 0)}
                        className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="0,00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Multa por atraso
                      <Info className="inline ml-1 h-4 w-4 text-gray-400" />
                    </label>
                    <select
                      value={formData.multaTipo}
                      onChange={(e) => handleInputChange('multaTipo', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="PERCENTAGE">Percentual</option>
                      <option value="FIXED">Valor fixo</option>
                    </select>
                  </div>

                  <div>
                    {formData.multaTipo === 'PERCENTAGE' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor percentual da multa
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.01"
                            value={formData.multaPercentual}
                            onChange={(e) => handleInputChange('multaPercentual', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="0,00"
                          />
                          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor fixo da multa
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <input
                            type="number"
                            step="0.01"
                            value={formData.multaFixa}
                            onChange={(e) => handleInputChange('multaFixa', parseFloat(e.target.value) || 0)}
                            className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="0,00"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Desconto */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">
                  Desconto
                  <Info className="inline ml-1 h-4 w-4 text-gray-400" />
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de desconto
                    </label>
                    <select
                      value={formData.descontoTipo}
                      onChange={(e) => handleInputChange('descontoTipo', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="PERCENTAGE">Percentual</option>
                      <option value="FIXED">Valor fixo</option>
                    </select>
                  </div>

                  <div>
                    {formData.descontoTipo === 'PERCENTAGE' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor percentual do desconto
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.01"
                            value={formData.descontoPercentual}
                            onChange={(e) => handleInputChange('descontoPercentual', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="0,00"
                          />
                          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor fixo do desconto
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <input
                            type="number"
                            step="0.01"
                            value={formData.descontoFixo}
                            onChange={(e) => handleInputChange('descontoFixo', parseFloat(e.target.value) || 0)}
                            className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="0,00"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prazo máximo do desconto
                      <Info className="inline ml-1 h-4 w-4 text-gray-400" />
                    </label>
                    <select
                      value={formData.prazoDesconto}
                      onChange={(e) => handleInputChange('prazoDesconto', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Selecione o prazo de desconto</option>
                      <option value="1">1 dia</option>
                      <option value="3">3 dias</option>
                      <option value="5">5 dias</option>
                      <option value="7">7 dias</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Descrição */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição
                </label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => handleInputChange('descricao', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="A descrição informada será impressa no boleto."
                />
              </div>

              {/* Envio por correios */}
              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.enviarCorreios}
                    onChange={(e) => handleInputChange('enviarCorreios', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Quero enviar esta cobrança via Correios ?
                  </span>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {loading ? 'Salvando...' : (assinaturaId ? 'Salvar alterações' : 'Adicionar assinatura')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AssinaturaModal;
